import { useEffect, useState } from "react";
import type { AppTag } from "../../../shared/connectors/tags";
import { queries } from "../../../shared/queries";
import { useQuery } from "@rocicorp/zero/react";
import { connectorDirectory } from "../../lib/connectorDirectory";
import { trackUiEvent } from "../../lib/tracing";
import { navigate, Route, routes, useRouteMatch } from "../../lib/urls";
import { Connected } from "./Connected";
import { ConnectorsLayout } from "./ConnectorsLayout";
import { DirectoryPage } from "./DirectoryPage";
import { DirectoryTabs } from "./DirectoryTabs";

const allTags: AppTag[] = [
  ...new Set(connectorDirectory.flatMap((app) => app.tags)),
].sort();

const paneStyle = (visible: boolean): React.CSSProperties => ({
  display: visible ? "flex" : "none",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
});

export function Connectors() {
  const [isConnected] = useRouteMatch(routes.connectors.connected);
  const [isDirectory] = useRouteMatch(routes.connectors.library);
  const [visitedConnected, setVisitedConnected] = useState(isConnected);
  const [visitedDirectory, setVisitedDirectory] = useState(isDirectory);

  if (isConnected && !visitedConnected) setVisitedConnected(true);
  if (isDirectory && !visitedDirectory) setVisitedDirectory(true);

  return (
    <>
      <Route path={routes.connectors}>
        <RedirectBasedOnConnectors />
      </Route>
      <ConnectorsLayout
        headerItem={isDirectory ? <DirectoryTabs tags={allTags} /> : undefined}
      >
        <div style={paneStyle(isConnected)}>
          {visitedConnected && <Connected active={isConnected} />}
        </div>
        <div style={paneStyle(isDirectory)}>
          {visitedDirectory && <DirectoryPage active={isDirectory} />}
        </div>
      </ConnectorsLayout>
    </>
  );
}

function RedirectBasedOnConnectors() {
  const [isConnected] = useRouteMatch(routes.connectors.connected);
  const [isDirectory] = useRouteMatch(routes.connectors.library);
  const [isLegacyDirectory] = useRouteMatch(routes.connectors.legacyDirectory);
  const [connectors] = useQuery(queries.connectorsIndex());

  useEffect(() => {
    if (isLegacyDirectory) {
      trackUiEvent("ui.connectors-directory-redirect");
      history.replaceState(
        history.state,
        "",
        location.href.replace("/connectors/directory", "/connectors/library")
      );
      return;
    }
    if (!isConnected && !isDirectory) {
      if (connectors.length === 0) {
        navigate(routes.connectors.library, undefined, { replace: true });
      } else {
        navigate(routes.connectors.connected, undefined, { replace: true });
      }
    }
  }, [isConnected, isDirectory, isLegacyDirectory, connectors.length]);

  return null;
}
